import { Component, Input, Output, EventEmitter, OnInit, AfterViewInit, OnDestroy, inject, PLATFORM_ID, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MapService } from '../../../../core/services/map.service';
import { LookupService } from '../../../../core/services/lookup.service';
import { AlertService } from '../../../../shared/services/alert.service';
import { LookupItem } from '../../../../core/models/lookup.model';

declare var L: any;

@Component({
  selector: 'app-map-picker',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="modal-backdrop" (click)="onClose.emit()">
      <div class="modal-card" (click)="$event.stopPropagation()">
        <div class="modal-header">
          <h3>Xəritədə seçin</h3>
          <button class="close-btn" (click)="onClose.emit()">&times;</button>
        </div>
        <div class="modal-body">
          <div class="map-actions">
            <button class="btn-location" (click)="getCurrentLocation()">
              <i class="fas fa-location-arrow"></i> Cari yerim
            </button>
          </div>
          <div id="picker-map" style="height: 400px; width: 100%; border-radius: 8px;"></div>

          <div class="map-info" *ngIf="selectedAddress">
            <i class="fas fa-map-marker-alt"></i> {{ selectedAddress }}
          </div>

          <div class="nearby-metros" *ngIf="nearbyMetros().length > 0">
            <div class="section-title">Yaxınlıqdakı metrolar (700 m):</div>
            <div class="metro-list">
              <span
                class="metro-tag"
                *ngFor="let metro of nearbyMetros()"
              >
                <i class="fas fa-subway"></i> {{ metro.name }}
              </span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" (click)="onClose.emit()">Ləğv et</button>
          <button class="btn-confirm" (click)="confirm()">Təsdiqlə</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(0,0,0,0.6);
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }
    .modal-card {
      background: white;
      width: 100%;
      max-width: 800px;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 10px 25px rgba(0,0,0,0.2);
    }
    .modal-header {
      padding: 16px 20px;
      border-bottom: 1px solid #f1f2f6;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .modal-header h3 { margin: 0; font-size: 18px; font-weight: 700; color: #2d3436; }
    .close-btn { background: none; border: none; font-size: 24px; cursor: pointer; color: #b2bec3; }
    .modal-body { padding: 20px; position: relative; }
    .map-actions {
      position: absolute;
      top: 30px;
      right: 30px;
      z-index: 1000;
    }
    .btn-location {
      padding: 10px;
      background: white;
      border: 1px solid #ccc;
      border-radius: 8px;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0,0,0,0.1);
      display: flex;
      align-items: center;
      gap: 5px;
      font-weight: 600;
      color: #2d3436;
    }
    .btn-location:hover { background: #f8f9fa; }
    .map-info {
      margin-top: 12px;
      padding: 10px;
      background: #f8f9fa;
      border-radius: 8px;
      font-size: 14px;
      color: #636e72;
      display: flex;
      gap: 8px;
      align-items: flex-start;
    }
    .nearby-metros {
      margin-top: 15px;
      padding: 10px;
      background: #f1f2f6;
      border-radius: 8px;
    }
    .section-title {
      font-size: 13px;
      font-weight: 700;
      color: #2d3436;
      margin-bottom: 8px;
    }
    .metro-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }
    .metro-tag {
      background: white;
      color: #00b894;
      padding: 4px 10px;
      border-radius: 15px;
      font-size: 12px;
      font-weight: 600;
      border: 1px solid #00b894;
      display: flex;
      align-items: center;
      gap: 5px;
      transition: all 0.2s;
    }
    .metro-tag:hover {
      background: #e6f7f4;
    }
    .metro-tag.selected {
      background: #00b894;
      color: white;
    }
    .modal-footer {
      padding: 16px 20px;
      border-top: 1px solid #f1f2f6;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }
    .btn-cancel { padding: 10px 20px; border: none; background: #f1f2f6; color: #2d3436; border-radius: 8px; font-weight: 600; cursor: pointer; }
    .btn-confirm { padding: 10px 20px; border: none; background: #00b894; color: white; border-radius: 8px; font-weight: 600; cursor: pointer; }
  `]
})
export class MapPickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() lat: number = 40.4093;
  @Input() lng: number = 49.8671;
  @Output() onLocationSelected = new EventEmitter<{lat: number, lng: number, address: string, metroId?: number | string}>();
  @Output() onClose = new EventEmitter<void>();

  private map: any;
  private marker: any;
  private platformId = inject(PLATFORM_ID);
  private mapService = inject(MapService);
  private lookupService = inject(LookupService);
  private alertService = inject(AlertService);
  private cdr = inject(ChangeDetectorRef);

  selectedAddress: string = '';
  allMetros: LookupItem[] = [];
  nearbyMetros = signal<LookupItem[]>([]);
  selectedMetroId: number | string | null = null;

  ngOnInit() {
    this.updateAddress(this.lat, this.lng);
    this.loadMetros();
  }

  loadMetros() {
    this.lookupService.getBakuMetroStations().subscribe(res => {
      this.allMetros = res;
      this.findNearbyMetros(this.lat, this.lng);
    });
  }

  findNearbyMetros(lat: number, lng: number) {
    if (!this.allMetros.length) return;

    const nearby = this.allMetros
      .map(m => {
        const dist = m.lat && m.lng ? this.mapService.calculateDistance(lat, lng, m.lat, m.lng) : Infinity;
        return { ...m, distance: dist };
      })
      .filter(m => m.distance <= 700)
      .sort((a, b) => a.distance - b.distance);

    this.nearbyMetros.set(nearby);

    // Auto-select the closest metro if none selected or if previously selected is not in nearby anymore
    if (nearby.length > 0) {
      this.selectedMetroId = nearby[0].id;
    } else {
      this.selectedMetroId = null;
    }
  }

  selectMetro(metro: LookupItem) {
    this.selectedMetroId = metro.id;
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.initMap();
    }
  }


  getCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          this.lat = lat;
          this.lng = lng;
          if (this.map) {
            this.map.setView([lat, lng], 17);
            this.marker.setLatLng([lat, lng]);
          }
          this.updateAddress(lat, lng);
          this.findNearbyMetros(lat, lng);
          this.cdr.detectChanges();
        },
        (error) => {
          console.error('Error getting location', error);
          if (error.code === error.PERMISSION_DENIED) {
            this.alertService.error('Məkana giriş icazəsi brauzer tərəfindən bloklanıb. Zəhmət olmasa brauzer sazlamalarından icazə verin.');
          } else {
            this.alertService.error('Cari məkan müəyyən edilərkən xəta baş verdi.');
          }
        }
      );
    } else {
      this.alertService.error('Brauzeriniz məkana girişi dəstəkləmir.');
    }
  }

  private initMap() {
    setTimeout(() => {
      this.map = L.map('picker-map').setView([this.lat, this.lng], 16);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(this.map);

      this.marker = L.marker([this.lat, this.lng], { draggable: true }).addTo(this.map);

      this.marker.on('dragend', (event: any) => {
        const position = event.target.getLatLng();
        this.lat = position.lat;
        this.lng = position.lng;
        this.updateAddress(this.lat, this.lng);
        this.findNearbyMetros(this.lat, this.lng);
        this.cdr.detectChanges();
      });

      this.map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        this.lat = lat;
        this.lng = lng;
        this.marker.setLatLng([lat, lng]);
        this.updateAddress(lat, lng);
        this.findNearbyMetros(lat, lng);
        this.cdr.detectChanges();
      });

      // Fix for Leaflet not rendering correctly in modals
      setTimeout(() => {
        this.map.invalidateSize();
      }, 200);
    }, 100);
  }

  private updateAddress(lat: number, lng: number) {
    this.mapService.getAddressFromCoords(lat, lng).subscribe(address => {
      this.selectedAddress = address;
      this.cdr.detectChanges();
    });
  }

  confirm() {
    this.onLocationSelected.emit({
      lat: this.lat,
      lng: this.lng,
      address: this.selectedAddress,
      metroId: this.selectedMetroId || undefined
    });
    this.onClose.emit();
  }

  ngOnDestroy() {
    if (this.map) {
      this.map.remove();
    }
  }
}
